'use client'

import type {
  ExploreFilters,
  ExploreLevel,
  ExplorePrice,
  ExploreSort,
} from './explore-params'
import { cn } from '@mentora/ui-components'
import { useTranslations } from 'next-intl'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import { useCallback, useTransition } from 'react'
import { useAnalytics } from '../analytics/use-analytics'
import { EXPLORE_LEVELS, EXPLORE_PRICES, EXPLORE_SORTS } from './explore-params'

const SORT_LABEL_KEY: Record<ExploreSort, string> = {
  all: 'sortAll',
  newest: 'sortNewest',
  popular: 'sortPopular',
  rated: 'sortRated',
  saved: 'sortSaved',
}

const PRICE_LABEL_KEY: Record<ExplorePrice, string> = {
  any: 'filterAny',
  free: 'filterFree',
  paid: 'filterPaid',
}

function useExploreNav() {
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const [, startTransition] = useTransition()
  const { track } = useAnalytics()

  return useCallback(
    (mutate: (params: URLSearchParams) => void) => {
      const params = new URLSearchParams(searchParams.toString())
      mutate(params)
      track('catalogue_filtered', { filter_keys: [...new Set(params.keys())].sort() })
      const qs = params.toString()
      startTransition(() => {
        router.replace(qs ? `${pathname}?${qs}` : pathname, { scroll: false })
      })
    },
    [router, pathname, searchParams, track],
  )
}

export function ExploreSortChips({ sort }: { sort: ExploreSort }) {
  const t = useTranslations('explore')
  const navigate = useExploreNav()

  function setSort(value: ExploreSort) {
    navigate((p) => {
      if (value === 'all')
        p.delete('sort')
      else p.set('sort', value)
    })
  }

  return (
    <div className="flex flex-wrap gap-2">
      {EXPLORE_SORTS.map((s) => {
        const active = s === sort
        return (
          <button
            key={s}
            type="button"
            onClick={() => setSort(s)}
            aria-pressed={active}
            className={cn(
              'rounded-full border px-3.5 py-2 text-[13px] font-medium transition-colors',
              active
                ? 'border-primary bg-primary text-primary-foreground'
                : 'border-border bg-card text-muted-foreground hover:text-foreground',
            )}
          >
            {t(SORT_LABEL_KEY[s])}
          </button>
        )
      })}
    </div>
  )
}

interface CategoryOption {
  name: string
  count: number
}

export function ExploreFilterSidebar({
  filters,
  categoryOptions,
}: {
  filters: ExploreFilters
  categoryOptions: CategoryOption[]
}) {
  const t = useTranslations('explore')
  const tLevel = useTranslations('courses.difficulty')
  const navigate = useExploreNav()

  function toggleList(key: 'category' | 'level', value: string, current: string[]) {
    const next = current.includes(value)
      ? current.filter(v => v !== value)
      : [...current, value]
    navigate((p) => {
      if (next.length === 0)
        p.delete(key)
      else p.set(key, next.join(','))
    })
  }

  function setPrice(value: ExplorePrice) {
    navigate((p) => {
      if (value === 'any')
        p.delete('price')
      else p.set('price', value)
    })
  }

  return (
    <>
      <div className="mb-2 text-xs font-semibold text-muted-foreground">
        {t('filterSpecialty')}
      </div>
      <div className="flex flex-col gap-2">
        {categoryOptions.length === 0
          ? (
              <span className="text-xs text-muted-foreground">
                {t('uncategorized')}
              </span>
            )
          : (
              categoryOptions.map(opt => (
                <label key={opt.name} className="flex items-center gap-2 text-[13px]">
                  <input
                    type="checkbox"
                    checked={filters.categories.includes(opt.name)}
                    onChange={() =>
                      toggleList('category', opt.name, filters.categories)}
                    className="size-3.5 accent-primary"
                  />
                  <span className="truncate">{opt.name}</span>
                  <span className="ml-auto tnum text-[11px] text-muted-foreground">
                    {opt.count}
                  </span>
                </label>
              ))
            )}
      </div>

      <div className="mb-2 mt-5 text-xs font-semibold text-muted-foreground">
        {t('filterLevel')}
      </div>
      <div className="flex flex-col gap-2">
        {EXPLORE_LEVELS.map(l => (
          <label key={l} className="flex items-center gap-2 text-[13px]">
            <input
              type="checkbox"
              checked={filters.levels.includes(l as ExploreLevel)}
              onChange={() => toggleList('level', l, filters.levels)}
              className="size-3.5 accent-primary"
            />
            {tLevel(l)}
          </label>
        ))}
      </div>

      <div className="mb-2 mt-5 text-xs font-semibold text-muted-foreground">
        {t('filterPrice')}
      </div>
      <div className="flex gap-1.5">
        {EXPLORE_PRICES.map(p => (
          <button
            key={p}
            type="button"
            onClick={() => setPrice(p)}
            aria-pressed={filters.price === p}
            className={cn(
              'rounded-full border px-3 py-1.5 text-xs font-medium',
              filters.price === p
                ? 'border-primary bg-primary text-primary-foreground'
                : 'border-input bg-card text-muted-foreground hover:text-foreground',
            )}
          >
            {t(PRICE_LABEL_KEY[p])}
          </button>
        ))}
      </div>
    </>
  )
}
